import { IconMail } from "@tabler/icons-react"
import { type FC, useState } from "react"
import { Link, useNavigate } from "react-router"

import { cn } from "@forento/ui/utilities/tailwind"

import { Input, PasswordInput } from "~/components/input"
import { useUserContext } from "~/context/user"
import { query } from "~/utilities/trpc"

const SignInPage: FC = () => {
	const userContext = useUserContext()
	const navigate = useNavigate()

	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [error, setError] = useState<string>()

	const signIn = query.signIn.useMutation({
		onMutate() {
			setError(undefined)
		},
		async onSuccess({ status }) {
			if (status === "error") {
				setError("Felaktig e-postadress eller lösenord")
				return
			}

			await userContext.reload({ clearSessionCache: true })
			navigate("/courses")
		},
		onError(error) {
			console.error(error)
		},
	})

	return (
		<div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-12">
			<div className="w-full max-w-md">
				<div className="mb-8 text-center">
					<h1 className="text-2xl font-bold text-slate-900">Logga in på ditt konto</h1>
					<p className="mt-2 text-sm text-slate-600">
						Välkommen tillbaka! Ange dina uppgifter för att fortsätta.
					</p>
				</div>

				{error && <div className="mb-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</div>}

				<form
					onSubmit={event => {
						event.preventDefault()
						signIn.mutate({ email, password })
					}}
					className="space-y-6"
				>
					<Input
						type="email"
						label="E-postadress"
						value={email}
						onChange={setEmail}
						placeholder="Ange din e-postadress"
						autoComplete="email"
						icon={IconMail}
						required
					/>

					<PasswordInput
						label="Lösenord"
						value={password}
						onChange={setPassword}
						placeholder="Ange ditt lösenord"
						autoComplete="current-password"
					/>

					<div className="text-right">
						<Link
							to="/user/forgot-password"
							className="text-primary hover:text-primary/80 text-sm hover:underline"
						>
							Glömt lösenord?
						</Link>
					</div>

					<button
						type="submit"
						disabled={signIn.isPending}
						className={cn(
							"w-full rounded-lg px-4 py-3 text-sm font-semibold text-white transition-colors duration-200",
							signIn.isPending
								? "cursor-not-allowed bg-slate-400"
								: "bg-primary hover:bg-primary/90 focus:ring-primary cursor-pointer focus:ring-2 focus:ring-offset-2 focus:outline-none",
						)}
					>
						{signIn.isPending ? "Loggar in..." : "Logga in"}
					</button>

					<div className="relative">
						<div className="absolute inset-0 flex items-center">
							<div className="w-full border-t border-slate-300" />
						</div>
						<div className="relative flex justify-center text-xs uppercase">
							<span className="bg-slate-50 px-2 text-slate-500">eller</span>
						</div>
					</div>

					<div className="text-center">
						<p className="text-sm text-slate-600">
							Har du inget konto?{" "}
							<Link
								to="/user/sign-up"
								className="text-primary hover:text-primary/80 font-medium hover:underline"
							>
								Skapa ett konto
							</Link>
						</p>
					</div>
				</form>
			</div>
		</div>
	)
}

export default SignInPage
